import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  TableSortLabel,
  TextField,
  InputAdornment,
  Breadcrumbs,
  Link,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from '@mui/material';
import {
  ArrowBack,
  Search,
  Person,
  Assignment,
  FilterList,
  Clear,
} from '@mui/icons-material';
import { authAPI } from '../services/api';
import { Activity, User } from '../api/BackofficeApi';

interface UserActivitiesProps {}

const UserActivities: React.FC<UserActivitiesProps> = () => {
  const { username, organization } = useParams<{ username: string; organization: string }>();
  const navigate = useNavigate();
  
  const [user, setUser] = useState<User | null>(null);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState<'all' | 'today' | 'week' | 'month'>('all');
  const [sortField, setSortField] = useState<'timestamp' | 'description'>('timestamp');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  useEffect(() => {
    if (username && organization) {
      fetchUserActivities();
    }
  }, [username, organization]);

  const fetchUserActivities = async () => {
    try {
      setLoading(true);
      
      // Get activities filtered by both organization and username
      const userActivities = await authAPI.getActivities({ 
        limit: 1000, 
        username: username,
        organization: organization 
      });
      
      setActivities(userActivities);
      
      // Set user info with both username and organization
      setUser({
        username: username!,
        organization: organization!,
        password: '',
        windowsCredentials: null,
        authorization: null
      });
      
    } catch (err: any) {
      setError('Failed to load user activities');
      console.error('User activities error:', err);
    } finally {
      setLoading(false);
    }
  };

  const getDateRange = () => {
    const now = Date.now();
    switch (dateFilter) {
      case 'today':
        return { from: now - (24 * 60 * 60 * 1000), to: now };
      case 'week':
        return { from: now - (7 * 24 * 60 * 60 * 1000), to: now };
      case 'month':
        return { from: now - (30 * 24 * 60 * 60 * 1000), to: now };
      default:
        return null;
    }
  };

  const filterActivities = (activities: Activity[]) => {
    let filtered = activities;
    
    // Apply date filter
    if (dateFilter !== 'all') {
      const dateRange = getDateRange();
      if (dateRange) {
        filtered = filtered.filter(activity => 
          activity.timestamp && 
          activity.timestamp >= dateRange.from && 
          activity.timestamp <= dateRange.to
        );
      }
    }
    
    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(activity =>
        activity.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    return filtered;
  };

  const sortActivities = (activities: Activity[]) => {
    return [...activities].sort((a, b) => {
      let aValue: any = a[sortField];
      let bValue: any = b[sortField];
      
      if (sortField === 'timestamp') {
        aValue = aValue || 0;
        bValue = bValue || 0;
      } else {
        aValue = (aValue || '').toLowerCase();
        bValue = (bValue || '').toLowerCase();
      }
      
      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const filteredAndSortedActivities = sortActivities(filterActivities(activities));

  const handleSort = (field: 'timestamp' | 'description') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setDateFilter('all');
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/organizations')}
        >
          Back to Organizations
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Breadcrumbs */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate('/organizations')}
          sx={{ textDecoration: 'none' }}
        >
          Organizations
        </Link>
        <Link
          component="button"
          variant="body1"
          onClick={() => navigate(`/organizations/${organization}`)}
          sx={{ textDecoration: 'none' }}
        >
          {organization}
        </Link>
        <Typography color="text.primary">{username}</Typography>
        <Typography color="text.primary">Activities</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center">
          <Person sx={{ mr: 2, fontSize: 32, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4">{username}</Typography>
            <Typography variant="subtitle1" color="text.secondary">
              {organization} • User Activities
            </Typography>
          </Box>
        </Box>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate(`/organizations/${organization}`)}
        >
          Back to Organization
        </Button>
      </Box>

      {/* Activity Stats */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Activities
              </Typography>
              <Typography variant="h4">
                {activities.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Today
              </Typography>
              <Typography variant="h4">
                {activities.filter(activity => {
                  const today = new Date();
                  const activityDate = activity.timestamp ? new Date(activity.timestamp) : new Date(0);
                  return activityDate.toDateString() === today.toDateString();
                }).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                This Week
              </Typography>
              <Typography variant="h4">
                {activities.filter(activity => {
                  const weekAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
                  return activity.timestamp && activity.timestamp >= weekAgo;
                }).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                This Month
              </Typography>
              <Typography variant="h4">
                {activities.filter(activity => {
                  const monthAgo = Date.now() - (30 * 24 * 60 * 60 * 1000);
                  return activity.timestamp && activity.timestamp >= monthAgo;
                }).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search activities..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Date Filter</InputLabel>
              <Select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value as any)}
                label="Date Filter"
              >
                <MenuItem value="all">All Time</MenuItem>
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="week">This Week</MenuItem>
                <MenuItem value="month">This Month</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <Button
              variant="outlined"
              onClick={clearFilters}
              startIcon={<Clear />}
              fullWidth
              disabled={!searchTerm && dateFilter === 'all'}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Activities Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Activity Type</TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'description'}
                  direction={sortField === 'description' ? sortDirection : 'asc'}
                  onClick={() => handleSort('description')}
                >
                  Description
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={sortField === 'timestamp'}
                  direction={sortField === 'timestamp' ? sortDirection : 'asc'}
                  onClick={() => handleSort('timestamp')}
                >
                  Timestamp
                </TableSortLabel>
              </TableCell>
              <TableCell>Organization</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAndSortedActivities.map((activity, index) => {
              const getActivityType = (description: string) => {
                if (!description) return 'Unknown';
                const desc = description.toLowerCase();
                if (desc.includes('login') || desc.includes('authentication')) return 'Authentication';
                if (desc.includes('create') || desc.includes('add')) return 'Creation';
                if (desc.includes('update') || desc.includes('modify')) return 'Update';
                if (desc.includes('delete') || desc.includes('remove')) return 'Deletion';
                if (desc.includes('export') || desc.includes('download')) return 'Export';
                if (desc.includes('import') || desc.includes('upload')) return 'Import';
                if (desc.includes('view') || desc.includes('access')) return 'Access';
                return 'General';
              };

              const getActivityIcon = (type: string) => {
                switch (type) {
                  case 'Authentication': return '🔐';
                  case 'Creation': return '➕';
                  case 'Update': return '✏️';
                  case 'Deletion': return '🗑️';
                  case 'Export': return '📤';
                  case 'Import': return '📥';
                  case 'Access': return '👁️';
                  default: return '📋';
                }
              };

              const activityType = getActivityType(activity.description || '');
              const activityIcon = getActivityIcon(activityType);

              return (
                <TableRow key={index}>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <Typography sx={{ mr: 1, fontSize: '1.2em' }}>
                        {activityIcon}
                      </Typography>
                      <Chip
                        label={activityType}
                        size="small"
                        color={
                          activityType === 'Authentication' ? 'success' :
                          activityType === 'Creation' ? 'primary' :
                          activityType === 'Update' ? 'warning' :
                          activityType === 'Deletion' ? 'error' :
                          activityType === 'Export' ? 'info' :
                          activityType === 'Import' ? 'secondary' :
                          'default'
                        }
                        variant="outlined"
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center">
                      <Assignment sx={{ mr: 1, color: 'text.secondary' }} />
                      <Typography variant="body2">
                        {activity.description || 'No description available'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {activity.timestamp 
                        ? new Date(activity.timestamp).toLocaleString()
                        : 'Unknown time'
                      }
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={activity.organization || 'Unknown'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {filteredAndSortedActivities.length === 0 && (
        <Box textAlign="center" py={4}>
          <Typography variant="h6" color="text.secondary">
            No activities found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your filters or search terms
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default UserActivities;
